import { isSameSymbol } from '@/entities/symbol';
import { MAX_TILES_LIMIT } from '@/features/Dashboard/constants/const';
import type {
  MarketApp,
  MarketApps,
  MarketAppWithTabs,
  TilesWidgetV2
} from '@/features/Dashboard/types';
import { extractIds } from '@/utils/extractIds';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

import type { SnackbarKey } from 'notistack';

export type SelectableWidget = { type: string; definition: any };

export function buildImportedAppFromWidget(
  selectedWidget: SelectableWidget,
  dashboardConfig: MarketApp[]
): MarketApp {
  if (selectedWidget.type === 'tiles') {
    const existingTiles = dashboardConfig.flatMap(app => {
      if ('type' in app && app.type === 'tiles') {
        return app.tiles.map(tile => tile);
      }
      return [];
    });
    const existingTileIds = extractIds(existingTiles);

    const tilesApp = selectedWidget.definition as unknown as TilesWidgetV2;
    tilesApp.tiles = tilesApp.tiles.map(tile => {
      const newTileId = generateUniqueUUID(existingTileIds);
      existingTileIds.add(newTileId);

      return {
        ...tile,
        id: newTileId
      };
    });
    return tilesApp;
  }

  const appsWithTabs = dashboardConfig.filter(app => 'tabs' in app);

  const existingTabs = appsWithTabs.flatMap(app => app.tabs.map(tab => tab));
  const existingTabIds = extractIds(existingTabs);
  const newTabId = generateUniqueUUID(existingTabIds);

  const existingAppIds = extractIds(appsWithTabs);
  const newAppId = generateUniqueUUID(existingAppIds);

  let name = selectedWidget.definition?.name as string | undefined;
  if (!name && selectedWidget.type === 'feed') {
    name = 'Feed';
  }
  if (!name && selectedWidget.type === 'file_list') {
    name = 'Data lake files';
  }
  return {
    tabs: [{ ...selectedWidget.definition, name, id: newTabId }],
    id: newAppId
  } as MarketApp;
}

export type ComputeNextAppsResult = {
  apps: MarketApps;
  exceeded: boolean;
  limitedCount: number;
  duplicatesCount: number;
  addedCodes: string[];
  duplicateCodes: string[];
};

export function computeNextApps(
  dashboardConfig: MarketApps | undefined,
  importedApp: MarketApp
): ComputeNextAppsResult {
  if (!dashboardConfig || dashboardConfig.length === 0) {
    // Handle tiles widget with limit when dashboard is empty
    if ('type' in (importedApp as any) && (importedApp as any).type === 'tiles') {
      const tilesWidget = importedApp as TilesWidgetV2;
      const exceeded = tilesWidget.tiles.length > MAX_TILES_LIMIT;
      const limitedTiles = tilesWidget.tiles.slice(0, MAX_TILES_LIMIT);
      const addedCodes = limitedTiles.map(tile => tile.symbol.key.Code);

      const limitedWidget = {
        ...tilesWidget,
        tiles: limitedTiles
      } as MarketApp;

      return {
        apps: [limitedWidget],
        exceeded,
        limitedCount: limitedTiles.length,
        duplicatesCount: 0,
        addedCodes,
        duplicateCodes: []
      };
    }

    return {
      apps: [importedApp],
      exceeded: false,
      limitedCount: 0,
      duplicatesCount: 0,
      addedCodes: [],
      duplicateCodes: []
    };
  }

  if ('type' in (importedApp as any) && (importedApp as any).type === 'tiles') {
    const current = [...dashboardConfig];
    const existingTilesIndex = current.findIndex(
      app => 'type' in (app as any) && (app as any).type === 'tiles'
    );

    if (existingTilesIndex !== -1) {
      const existing = current[existingTilesIndex] as TilesWidgetV2;
      const toImport = importedApp as TilesWidgetV2;

      // Remove duplicates based on symbol equality
      const existingSymbols = existing.tiles.map(tile => tile.symbol.key);

      const newTiles = toImport.tiles.filter(
        tile => !existingSymbols.some(es => isSameSymbol(es, tile.symbol.key))
      );

      // Get duplicate codes
      const duplicateTiles = toImport.tiles.filter(tile =>
        existingSymbols.some(es => isSameSymbol(es, tile.symbol.key))
      );
      const duplicateCodes = duplicateTiles.map(tile => tile.symbol.key.Code);

      // Count duplicates
      const duplicatesCount = toImport.tiles.length - newTiles.length;

      // Check if we'll exceed the limit
      const totalTiles = existing.tiles.length + newTiles.length;
      const exceeded = totalTiles > MAX_TILES_LIMIT;

      // Limit to MAX_TILES_LIMIT
      const availableSlots = MAX_TILES_LIMIT - existing.tiles.length;
      const tilesToAdd = newTiles.slice(0, Math.max(0, availableSlots));
      const addedCodes = tilesToAdd.map(tile => tile.symbol.key.Code);

      const merged = {
        ...existing,
        tiles: [...existing.tiles, ...tilesToAdd]
      } as MarketApp;

      const next = [...current];
      next.splice(existingTilesIndex, 1);

      return {
        apps: [merged, ...next],
        exceeded,
        limitedCount: tilesToAdd.length,
        duplicatesCount,
        addedCodes,
        duplicateCodes
      };
    }

    // New tiles widget - limit to MAX_TILES_LIMIT
    const tilesWidget = importedApp as TilesWidgetV2;
    const exceeded = tilesWidget.tiles.length > MAX_TILES_LIMIT;
    const limitedTiles = tilesWidget.tiles.slice(0, MAX_TILES_LIMIT);
    const addedCodes = limitedTiles.map(tile => tile.symbol.key.Code);

    const limitedWidget = {
      ...tilesWidget,
      tiles: limitedTiles
    } as MarketApp;

    return {
      apps: [limitedWidget, ...dashboardConfig],
      exceeded,
      limitedCount: limitedTiles.length,
      duplicatesCount: 0,
      addedCodes,
      duplicateCodes: []
    };
  }

  return {
    apps: [...dashboardConfig, importedApp],
    exceeded: false,
    limitedCount: 0,
    duplicatesCount: 0,
    addedCodes: [],
    duplicateCodes: []
  };
}

type TilesWidget = TilesWidgetV2;

type TabsWidget = MarketAppWithTabs;

type EditActions = {
  addTile: (tabId: string, tile: TilesWidget['tiles'][number]) => void;
  addApp: (tabId: string, app: MarketAppWithTabs) => void;
};

type ShowErrorSnackbar = (
  message: string,
  autoHideDuration?: number,
  key?: SnackbarKey
) => SnackbarKey;

type ShowSuccessSnackbar = (message: string, key?: SnackbarKey) => SnackbarKey;

const isTilesWidget = (widget: MarketApp): widget is TilesWidget =>
  'type' in widget && widget.type === 'tiles';

const isTabsWidget = (widget: MarketApp): widget is TabsWidget =>
  !('type' in widget) && Array.isArray(widget.tabs);

const buildSymbolKeyId = (key: { Code: string; Period: string; PeriodType: string }) => {
  return `${key.Code}|${key.Period}|${key.PeriodType}`;
};

const computeTileImportOutcome = (
  currentTiles: TilesWidget['tiles'],
  incomingTiles: TilesWidget['tiles']
) => {
  const existingKeys = new Set(currentTiles.map(tile => buildSymbolKeyId(tile.symbol.key)));
  const newTiles: TilesWidget['tiles'] = [];
  const duplicateTiles: TilesWidget['tiles'] = [];

  for (const tile of incomingTiles) {
    if (existingKeys.has(buildSymbolKeyId(tile.symbol.key))) {
      duplicateTiles.push(tile);
    } else {
      newTiles.push(tile);
    }
  }

  const availableSlots = Math.max(0, MAX_TILES_LIMIT - currentTiles.length);
  const tilesToAdd = newTiles.slice(0, availableSlots);
  const exceeded = currentTiles.length + newTiles.length > MAX_TILES_LIMIT;

  return {
    duplicatesCount: duplicateTiles.length,
    duplicateCodes: duplicateTiles.map(tile => tile.symbol.key.Code),
    tilesToAdd,
    exceeded
  };
};

export const buildSuccessMessage = (mutationResult: {
  duplicatesCount: number;
  addedCodes: string[];
}) => {
  if (mutationResult.duplicatesCount === 0 && mutationResult.addedCodes.length === 0) {
    return 'Dashboard updated!';
  }

  const parts: string[] = [];
  if (mutationResult.duplicatesCount > 0) {
    parts.push(
      `${mutationResult.duplicatesCount} item${mutationResult.duplicatesCount > 1 ? 's' : ''} already in Dashboard`
    );
  }
  if (mutationResult.addedCodes.length > 0) {
    parts.push(
      `${mutationResult.addedCodes.length} item${mutationResult.addedCodes.length > 1 ? 's' : ''} added!`
    );
  }
  return parts.join(', ');
};

const addTilesToDashboard = (
  actions: EditActions,
  currentTabId: string,
  tiles: TilesWidget['tiles']
) => {
  tiles.forEach(tile => {
    actions.addTile(currentTabId, tile);
  });
};

const notifyDuplicateTiles = (
  outcome: ReturnType<typeof computeTileImportOutcome>,
  showErrorSnackbar: ShowErrorSnackbar
) => {
  if (outcome.duplicatesCount === 0) return;

  const duplicatesLabel = `Duplicate Highlighted price${outcome.duplicatesCount > 1 ? 's' : ''}`;
  showErrorSnackbar(
    `${duplicatesLabel} skipped: ${outcome.duplicateCodes.join(', ')}`,
    5000,
    'tiles-duplicates-error'
  );
};

const notifyAddedTiles = (
  outcome: ReturnType<typeof computeTileImportOutcome>,
  showSuccessSnackbar: ShowSuccessSnackbar
) => {
  if (outcome.tilesToAdd.length === 0) return;

  const addedCodes = outcome.tilesToAdd.map(tile => tile.symbol.key.Code);
  const addedLabel = `Highlighted price${addedCodes.length > 1 ? 's' : ''}`;
  showSuccessSnackbar(`${addedLabel} added: ${addedCodes.join(', ')}`);
};

const notifyExceededTiles = (
  outcome: ReturnType<typeof computeTileImportOutcome>,
  showErrorSnackbar: ShowErrorSnackbar
) => {
  if (!outcome.exceeded) return;

  const tilesLabel = `tile${outcome.tilesToAdd.length === 1 ? '' : 's'}`;
  showErrorSnackbar(
    `Tile limit of ${MAX_TILES_LIMIT} exceeded. Only ${outcome.tilesToAdd.length} ${tilesLabel} added.`,
    5000,
    'tiles-limit-exceeded-error'
  );
};

export const handleEditModeImport = ({
  actions,
  currentTabId,
  currentTiles,
  templateWidget,
  showErrorSnackbar,
  showSuccessSnackbar
}: {
  actions: EditActions;
  currentTabId: string;
  currentTiles: TilesWidget['tiles'];
  templateWidget: MarketApp;
  showErrorSnackbar: ShowErrorSnackbar;
  showSuccessSnackbar: ShowSuccessSnackbar;
}) => {
  if (isTilesWidget(templateWidget)) {
    const outcome = computeTileImportOutcome(currentTiles, templateWidget.tiles);

    addTilesToDashboard(actions, currentTabId, outcome.tilesToAdd);
    notifyDuplicateTiles(outcome, showErrorSnackbar);
    notifyAddedTiles(outcome, showSuccessSnackbar);
    notifyExceededTiles(outcome, showErrorSnackbar);
    return;
  }

  if (isTabsWidget(templateWidget)) {
    actions.addApp(currentTabId, templateWidget);
  }
};
